import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek } from 'date-fns';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiCalendar } from 'react-icons/fi';

const TaskCalendar = ({ tasks }) => {
  const { isDark } = useTheme();
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Filter tasks that have due dates
  const tasksWithDueDates = tasks.filter(task => task.due_date);

  const getTasksForDate = (date) => {
    return tasksWithDueDates.filter(task => {
      if (!task.due_date) return false;
      const taskDate = new Date(task.due_date);
      return isSameDay(taskDate, date);
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: isDark ? 'bg-gray-600' : 'bg-gray-500',
      in_progress: isDark ? 'bg-blue-600' : 'bg-blue-500',
      completed: isDark ? 'bg-green-600' : 'bg-green-500',
      blocked: isDark ? 'bg-red-600' : 'bg-red-500',
    };
    return colors[status] || colors.pending;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: isDark ? 'bg-green-600' : 'bg-green-500',
      medium: isDark ? 'bg-yellow-600' : 'bg-yellow-500',
      high: isDark ? 'bg-red-600' : 'bg-red-500',
    };
    return colors[priority] || colors.medium;
  };

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`backdrop-blur-md rounded-xl border overflow-hidden ${
      isDark 
        ? 'bg-gray-800/50 border-gray-700' 
        : 'bg-white/10 border-white/20'
    }`}>
      {/* Calendar Header */}
      <div className={`p-4 border-b ${
        isDark ? 'border-gray-700' : 'border-white/20'
      }`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <FiCalendar className={isDark ? 'text-gray-300' : 'text-white'} size={24} />
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-white'}`}>
              {format(currentMonth, 'MMMM yyyy')}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className={`p-2 rounded-lg transition-all ${
                isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <FiChevronLeft size={20} />
            </button>
            <button
              onClick={() => setCurrentMonth(new Date())}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              Today
            </button>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className={`p-2 rounded-lg transition-all ${
                isDark
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <FiChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Week Day Headers */}
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map(day => (
            <div
              key={day}
              className={`text-center text-sm font-semibold py-2 ${
                isDark ? 'text-gray-400' : 'text-white/70'
              }`}
            >
              {day}
            </div>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="p-4">
        {tasksWithDueDates.length === 0 ? (
          <div className={`text-center py-12 ${
            isDark ? 'text-gray-400' : 'text-white/60'
          }`}>
            <FiCalendar className="mx-auto mb-4 opacity-50" size={48} />
            <p className="text-lg mb-2">No tasks with due dates</p>
            <p className="text-sm">Tasks with due dates will appear on the calendar</p>
          </div>
        ) : (
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, idx) => {
            const dayTasks = getTasksForDate(day);
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, new Date());

            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.01 }}
                className={`min-h-[100px] p-1 border rounded-lg ${
                  isDark
                    ? isCurrentMonth ? 'border-gray-700 bg-gray-800/30' : 'border-gray-800 bg-gray-900/20'
                    : isCurrentMonth ? 'border-white/20 bg-white/5' : 'border-white/10 bg-white/2'
                }`}
              >
                <div className={`text-xs font-medium mb-1 ${
                  isToday
                    ? isDark
                      ? 'text-blue-400 font-bold'
                      : 'text-blue-300 font-bold'
                    : isDark
                      ? isCurrentMonth ? 'text-gray-300' : 'text-gray-600'
                      : isCurrentMonth ? 'text-white/80' : 'text-white/40'
                }`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1">
                  {dayTasks.slice(0, 3).map(task => (
                    <Link
                      key={task.id}
                      to={`/tasks/${task.id}`}
                      className={`block text-xs p-1 rounded truncate ${
                        getStatusColor(task.status)
                      } text-white hover:opacity-80 transition-opacity flex items-center gap-1`}
                      title={task.title}
                    >
                      <span className="truncate">{task.title}</span>
                      {task.recurrence_parent_id && <span className="shrink-0 opacity-90" aria-label="Monthly">📅</span>}
                    </Link>
                  ))}
                  {dayTasks.length > 3 && (
                    <div className={`text-xs px-1 py-0.5 rounded ${
                      isDark ? 'text-gray-400 bg-gray-700' : 'text-white/60 bg-white/10'
                    }`}>
                      +{dayTasks.length - 3} more
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskCalendar;
